import { reactive } from 'vue'

type ContentStatus = 'unpublished' | 'discarded' | 'defected' | 'light-defected'
type ContentTheme = 'day' | 'night'

interface ProductImage {
  url: string
}

interface PairedProduct {
  title?: string
  url?: string
  description?: string
  price?: number
  imageUrl?: string
  isExpanded?: boolean
}

interface ContentData {
  brand: string
  productTitle: string
  status: ContentStatus
  theme: ContentTheme
  backgroundColor: string
  productImages: ProductImage[]
  pairedProducts: PairedProduct[]
}

// Default content data
const defaultContentData: ContentData = {
  brand: 'YourBrand',
  productTitle: 'Handcrafted Ceramic Vase',
  status: 'unpublished',
  theme: 'day',
  backgroundColor: '#ffffff',
  productImages: [
    { url: 'https://res.cloudinary.com/ommi-shop/image/upload/ommishop-marketing/public/mocks-image-product-page/model-3-vase-primary-view.jpg.jpg' },
    { url: 'https://res.cloudinary.com/ommi-shop/image/upload/ommishop-marketing/public/mocks-image-product-page/model-3-vase-surface-texture.jpg.jpg' },
    { url: 'https://res.cloudinary.com/ommi-shop/image/upload/ommishop-marketing/public/mocks-image-product-page/model-3-vase-rim-detail.jpg.jpg' },
    { url: 'https://res.cloudinary.com/ommi-shop/image/upload/ommishop-marketing/public/mocks-image-product-page/model-3-vase-profile-view.jpg.jpg' },
    { url: '' }
  ],
  pairedProducts: [] as PairedProduct[]
}

// Reactive content data
const contentData = reactive<ContentData>({ ...defaultContentData })

// Load data from localStorage
const loadContentData = () => {
  if (import.meta.client) {
    const saved = localStorage.getItem('content-studio-data')
    if (saved) {
      try {
        const parsed = JSON.parse(saved) as Partial<ContentData>
        // Update each property individually to ensure reactivity
        contentData.brand = parsed.brand || ''
        contentData.productTitle = parsed.productTitle || ''
        contentData.status = (parsed.status as ContentStatus) || 'unpublished'
        contentData.theme = (parsed.theme as ContentTheme) || 'day'
        contentData.backgroundColor = parsed.backgroundColor || '#ffffff'
        
        // Ensure we always have exactly 5 product images
        const existingImages = Array.isArray(parsed.productImages) ? parsed.productImages : []
        contentData.productImages = Array.from({ length: 5 }, (_, i) => {
          const image = existingImages[i]
          if (image && typeof image === 'object' && 'url' in image) {
            return { url: String((image as ProductImage).url || '') }
          }
          return { url: '' }
        })
        
        contentData.pairedProducts = Array.isArray(parsed.pairedProducts)
          ? parsed.pairedProducts.map((pair) => ({
              title: pair?.title,
              url: pair?.url,
              description: pair?.description,
              price: typeof pair?.price === 'number' ? pair.price : undefined,
              imageUrl: pair?.imageUrl,
              isExpanded: Boolean(pair?.isExpanded)
            }))
          : []
      } catch (error) {
        console.error('Error loading content studio data:', error)
        // Initialize with defaults if parsing fails
        initializeWithDefaults()
      }
    } else {
      // No saved data, initialize with defaults
      initializeWithDefaults()
    }
  }
}

// Initialize with default data
const initializeWithDefaults = () => {
  contentData.brand = defaultContentData.brand
  contentData.productTitle = defaultContentData.productTitle
  contentData.status = defaultContentData.status
  contentData.theme = defaultContentData.theme
  contentData.backgroundColor = defaultContentData.backgroundColor
  contentData.productImages = [...defaultContentData.productImages]
  contentData.pairedProducts = [...defaultContentData.pairedProducts]
}

// Compress image data to reduce storage size while maintaining quality
// maxSize: 1024px (high resolution for crisp display)
// quality: 1.0 (100% quality, lossless compression)
const compressImage = (dataUrl: string, maxSize = 1024, quality = 1.0): Promise<string> => {
  return new Promise((resolve) => {
    if (!dataUrl || !dataUrl.startsWith('data:image')) {
      resolve(dataUrl)
      return
    }
    
    const img = new Image()
    img.onload = () => {
      // Skip compression if image is already small enough
      if (img.width <= maxSize && img.height <= maxSize) {
        resolve(dataUrl)
        return
      }
      
      const canvas = document.createElement('canvas')
      const ctx = canvas.getContext('2d')
      if (!ctx) {
        resolve(dataUrl)
        return
      }
      
      // Calculate new dimensions while maintaining aspect ratio
      const ratio = Math.min(maxSize / img.width, maxSize / img.height)
      canvas.width = img.width * ratio
      canvas.height = img.height * ratio
      
      // Draw and compress with high quality
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
      const compressed = canvas.toDataURL('image/jpeg', quality)
      resolve(compressed)
    }
    img.onerror = () => {
      console.warn('Failed to load image for compression, using original')
      resolve(dataUrl)
    }
    img.src = dataUrl
  })
}

// Save data to localStorage with error handling
const saveContentData = async (data: ContentData) => {
  if (import.meta.client) {
    try {
      // Compress images before saving
      const compressedProductImages = await Promise.all(
        data.productImages.map(async (img: ProductImage) => ({
          url: img.url ? await compressImage(img.url) : ''
        }))
      )
      
      const compressedPairedProducts = await Promise.all(
        data.pairedProducts.map(async (pair: PairedProduct) => ({
          title: pair.title,
          url: pair.url,
          description: pair.description,
          price: pair.price,
          imageUrl: pair.imageUrl ? await compressImage(pair.imageUrl) : '',
          isExpanded: pair.isExpanded || false
        }))
      )
      
      const storageData = {
        brand: data.brand,
        productTitle: data.productTitle,
        status: data.status,
        theme: data.theme,
        backgroundColor: data.backgroundColor,
        productImages: compressedProductImages,
        pairedProducts: compressedPairedProducts
      }
      
      localStorage.setItem('content-studio-data', JSON.stringify(storageData))
      
      // Update reactive data to trigger reactivity
      contentData.brand = data.brand
      contentData.productTitle = data.productTitle
      contentData.status = data.status
      contentData.theme = data.theme
      contentData.backgroundColor = data.backgroundColor
      contentData.productImages = data.productImages
      contentData.pairedProducts = data.pairedProducts
    } catch (error) {
      if (error instanceof DOMException && error.name === 'QuotaExceededError') {
        console.warn('Storage quota exceeded. Data not saved.')
        throw new Error('Storage quota exceeded. Please clear some data or use smaller images.')
      } else {
        console.error('Error saving content data:', error)
        throw error
      }
    }
  }
}

// Get status display text
const getStatusDisplay = (status: string) => {
  const statusMap: Record<ContentStatus, string> = {
    unpublished: 'Unpublished',
    discarded: 'Discarded',
    defected: 'Defected',
    'light-defected': 'Light Defected'
  }
  return statusMap[status as ContentStatus] ?? status
}

// Get status color
const getStatusColor = (status: string) => {
  const colorMap: Record<ContentStatus, string> = {
    unpublished: 'bg-gray-100 text-gray-800',
    discarded: 'bg-red-100 text-red-800',
    defected: 'bg-orange-100 text-orange-800',
    'light-defected': 'bg-yellow-100 text-yellow-800'
  }
  return colorMap[status as ContentStatus] ?? 'bg-gray-100 text-gray-800'
}

// Clear localStorage data
const clearContentData = () => {
  if (import.meta.client) {
    localStorage.removeItem('content-studio-data')
    
    // Update reactive data to trigger reactivity
    contentData.brand = defaultContentData.brand
    contentData.productTitle = defaultContentData.productTitle
    contentData.status = defaultContentData.status
    contentData.theme = defaultContentData.theme
    contentData.backgroundColor = defaultContentData.backgroundColor
    contentData.productImages = [...defaultContentData.productImages]
    contentData.pairedProducts = [...defaultContentData.pairedProducts]
  }
}

// Calculate brightness of a color and return if it's light or dark
// Returns 'day' for dark backgrounds, 'night' for light backgrounds
const getThemeFromColor = (hexColor: string): string => {
  // Remove # if present
  const hex = hexColor.replace('#', '')
  
  // Convert to RGB
  const r = parseInt(hex.substring(0, 2), 16)
  const g = parseInt(hex.substring(2, 4), 16)
  const b = parseInt(hex.substring(4, 6), 16)
  
  // Calculate relative luminance using the formula
  // https://www.w3.org/TR/WCAG20-TECHS/G17.html
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255
  
  // If background is dark (luminance < 0.5), use night mode (white text)
  // If background is light (luminance >= 0.5), use day mode (black text)
  return luminance < 0.5 ? 'night' : 'day'
}

export const useContentStudio = () => {
  return {
    contentData,
    loadContentData,
    saveContentData,
    clearContentData,
    getStatusDisplay,
    getStatusColor,
    getThemeFromColor,
    defaultContentData
  }
}
